import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { imgSrc } from '../lib/imgSrc';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import PinButton from './PinButton';
import { usePins } from '../lib/PinsContext';

const STATUS = {
  operational: { label: 'Operational', className: 'bg-green-500 hover:bg-green-600' },
  maintenance_needed: { label: 'Maintenance Needed', className: 'bg-yellow-500 hover:bg-yellow-600' },
  down: { label: 'Down', className: 'bg-red-500 hover:bg-red-600' },
  lockout: { label: 'Lockout', className: 'bg-orange-500 hover:bg-orange-600' },
};

// Rendered outside the parent so ESLint doesn't flag component-in-render.
const DragHandle = ({ attributes, listeners, machineryId }) => (
  <button
    type="button"
    {...attributes}
    {...listeners}
    onClick={(e) => e.preventDefault()}
    className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 select-none px-1"
    aria-label="Drag to reorder"
    data-testid={`drag-handle-${machineryId}`}
    title="Drag to move between groups"
  >
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <circle cx="7" cy="4" r="1.4" /><circle cx="13" cy="4" r="1.4" />
      <circle cx="7" cy="10" r="1.4" /><circle cx="13" cy="10" r="1.4" />
      <circle cx="7" cy="16" r="1.4" /><circle cx="13" cy="16" r="1.4" />
    </svg>
  </button>
);

/**
 * Draggable equipment card with two visual modes:
 *   - viewMode='card'  → full card with image, status, meta rows
 *   - viewMode='chip'  → dense one-line chip (component + status pill)
 */
const EquipmentSortableCard = ({ machinery, viewMode, isAdmin, dndDisabled }) => {
  const navigate = useNavigate();
  const { pinnedIds, setPinnedFromArray } = usePins();
  const isPinned = pinnedIds.has(machinery.id);
  const handlePinChange = (_next, newIds) => setPinnedFromArray(newIds);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: machinery.id,
    disabled: !isAdmin || dndDisabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const status = STATUS[machinery.status] || { label: machinery.status, className: 'bg-gray-500' };
  const showHandle = isAdmin && !dndDisabled;

  const goToDetail = (e) => {
    if (e.defaultPrevented) return;
    navigate(`/machinery/${machinery.id}`);
  };

  if (viewMode === 'chip') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        data-testid={`equipment-chip-${machinery.id}`}
        className="flex items-center gap-1 bg-white border rounded-full pl-1 pr-3 py-1 shadow-sm hover:shadow-md transition cursor-pointer"
        onClick={goToDetail}
      >
        {showHandle && (
          <DragHandle attributes={attributes} listeners={listeners} machineryId={machinery.id} />
        )}
        <PinButton machineryId={machinery.id} pinned={isPinned} onChange={handlePinChange} size="sm" />
        <span className={`inline-block h-2 w-2 rounded-full ${status.className.split(' ')[0]}`} />
        <span className="text-sm font-medium text-slate-900 truncate max-w-[220px]">
          {machinery.component}
        </span>
        <span className="text-xs text-slate-500 truncate max-w-[140px]">
          {machinery.manufacturer}
        </span>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} data-testid={`equipment-card-${machinery.id}`}>
      <Card
        className="cursor-pointer hover:shadow-lg transition-shadow relative"
        onClick={goToDetail}
      >
        <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
          <PinButton machineryId={machinery.id} pinned={isPinned} onChange={handlePinChange} />
        </div>
        {showHandle && (
          <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
            <DragHandle attributes={attributes} listeners={listeners} machineryId={machinery.id} />
          </div>
        )}
        {machinery.main_image && (
          <div className="h-48 overflow-hidden rounded-t-lg">
            <img
              src={imgSrc(machinery.main_image, { thumb: true })}
              alt={machinery.component}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
        <CardHeader>
          <div className="flex justify-between items-start gap-2">
            <CardTitle className="text-lg">{machinery.component}</CardTitle>
            <Badge className={status.className}>{status.label}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Local Location:</span>
              <span className="font-medium">{machinery.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Manufacturer:</span>
              <span className="font-medium">{machinery.manufacturer}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Serial #:</span>
              <span className="font-medium font-mono text-xs">{machinery.serial_number}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EquipmentSortableCard;
